import React from 'react';
import { cn } from '../../lib/utils';
import { AlertTriangle } from 'lucide-react';

const WalletGauge = ({ 
  balance, 
  reservedHours = 0,
  maxBalance = 100, 
  hourlyRate = 75, 
  reservations = [],
  className,
  onClick,
  showDetails = true,
  onRegularize
}) => {
  // Calculate available hours (can be negative)
  const availableHours = balance - reservedHours;
  const isNegative = availableHours < 0;
  
  // For display purposes
  const displayAvailable = Math.max(0, availableHours);
  const percentage = Math.min(Math.max(0, (balance / maxBalance) * 100), 100);
  const reservedPercentage = Math.min((reservedHours / maxBalance) * 100, 100);
  const availableAmount = availableHours * hourlyRate;
  const reservedAmount = reservedHours * hourlyRate;
  const totalAmount = balance * hourlyRate;
  
  const getColor = () => {
    if (isNegative) return '#ef4444';
    const availablePercentage = (availableHours / maxBalance) * 100;
    if (availablePercentage >= 50) return '#00d4aa';
    if (availablePercentage >= 25) return '#f59e0b';
    return '#ef4444';
  };

  // For negative balance, show full red gauge
  // Colors: Green for available, Orange for reserved
  const gaugeBackground = isNegative 
    ? `conic-gradient(from 0deg, #ef4444 0% 100%)`
    : `conic-gradient(
        from 0deg,
        #10b981 0% ${Math.max(percentage - reservedPercentage, 0)}%,
        #f97316 ${Math.max(percentage - reservedPercentage, 0)}% ${percentage}%,
        #e2e8f0 ${percentage}% 100%
      )`;

  return (
    <div className={cn("flex flex-col items-center", className)}>
      {/* Negative Balance Alert */}
      {isNegative && (
        <div className="w-full mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <div>
              <p className="font-semibold text-sm">Solde insuffisant</p>
              <p className="text-xs">Vous avez {Math.abs(availableHours).toFixed(1)}h à régulariser</p>
            </div>
          </div>
          {onRegularize && (
            <button 
              onClick={onRegularize}
              className="mt-2 w-full text-xs bg-red-600 text-white py-1.5 px-3 rounded-md hover:bg-red-700 transition-colors"
            >
              Régulariser maintenant
            </button>
          )}
        </div>
      )}
      
      <div 
        className={cn(
          "relative h-32 w-32 flex items-center justify-center rounded-full cursor-pointer hover:scale-105 transition-transform",
          isNegative && "ring-4 ring-red-200 ring-offset-2"
        )}
        style={{ background: gaugeBackground }}
        data-testid="wallet-gauge"
        onClick={onClick}
      >
        <div className={cn(
          "h-24 w-24 rounded-full flex items-center justify-center flex-col shadow-inner",
          isNegative ? "bg-red-50" : "bg-white"
        )}>
          <span className={cn(
            "font-mono font-bold text-2xl",
            isNegative ? "text-red-600" : "text-[#1a365d]"
          )} data-testid="wallet-hours">
            {availableHours.toFixed(1)}h
          </span>
          <span className={cn(
            "text-xs",
            isNegative ? "text-red-500" : "text-[#64748b]"
          )}>
            {isNegative ? "à régulariser" : "disponibles"}
          </span>
        </div>
      </div>
      
      <div className="mt-3 text-center">
        <p className={cn(
          "font-mono font-bold text-lg",
          isNegative ? "text-red-600" : "text-[#1a365d]"
        )} data-testid="wallet-amount">
          {availableAmount.toFixed(2)}€
        </p>
        <p className="text-xs text-[#64748b]">
          Taux: {hourlyRate}€/h
        </p>
      </div>
      
      {/* Visual breakdown bar */}
      {showDetails && (balance > 0 || reservedHours > 0) && (
        <div className="mt-4 w-full max-w-[220px]">
          <p className="text-xs text-slate-500 mb-2 font-medium">Répartition du crédit</p>
          <div className="h-4 bg-slate-200 rounded-full overflow-hidden flex">
            {availableHours > 0 && (
              <div 
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${(availableHours / Math.max(balance, reservedHours)) * 100}%` }}
                title={`Disponible: ${availableHours.toFixed(1)}h`}
              />
            )}
            {reservedHours > 0 && (
              <div 
                className="h-full bg-orange-400 transition-all"
                style={{ width: `${Math.min((reservedHours / Math.max(balance, reservedHours)) * 100, 100)}%` }}
                title={`Réservé: ${reservedHours.toFixed(1)}h`}
              />
            )}
          </div>
          <div className="flex justify-between mt-2 text-xs">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span className="text-slate-500">Disponible</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-orange-400"></span>
              <span className="text-slate-500">Réservé</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Show reserved info if any */}
      {reservedHours > 0 && showDetails && (
        <div className="mt-3 w-full max-w-[220px] bg-slate-50 rounded-lg p-2 text-xs">
          <div className="flex justify-between text-slate-600 mb-1">
            <span>Total crédit:</span>
            <span className="font-medium">{balance.toFixed(1)}h ({totalAmount.toFixed(0)}€)</span>
          </div>
          <div className="flex justify-between text-slate-500">
            <span>Réservé (projets):</span>
            <span className="font-medium">{reservedHours.toFixed(1)}h ({reservedAmount.toFixed(0)}€)</span>
          </div>
          <div className={cn(
            "flex justify-between border-t border-slate-200 pt-1 mt-1",
            isNegative ? "text-red-600" : "text-green-600"
          )}>
            <span>{isNegative ? "Déficit:" : "Disponible:"}</span>
            <span className="font-semibold">{availableHours.toFixed(1)}h</span>
          </div>
        </div>
      )}
      
      {/* Show reservations list */}
      {reservations && reservations.length > 0 && showDetails && (
        <div className="mt-2 w-full max-w-[200px]">
          <p className="text-xs text-slate-500 mb-1">Projets réservés:</p>
          <div className="space-y-1">
            {reservations.slice(0, 3).map((res, idx) => (
              <div key={idx} className="flex justify-between text-xs bg-slate-100 rounded px-2 py-1">
                <span className="truncate max-w-[120px]" title={res.project_name}>
                  {res.project_name || 'Projet'}
                </span>
                <span className="text-slate-600 font-medium">{res.reserved_hours}h</span>
              </div>
            ))}
            {reservations.length > 3 && (
              <p className="text-xs text-slate-400 text-center">+{reservations.length - 3} autres</p>
            )}
          </div>
        </div>
      )}
      
      {(availableHours / maxBalance) * 100 < 25 && (
        <div className="mt-2 text-center">
          <span className="text-xs text-[#f59e0b] font-semibold bg-[#fef3c7] px-2 py-1 rounded-full">
            ⚠️ Crédit faible
          </span>
        </div>
      )}
    </div>
  );
};

export default WalletGauge;
